import { ReactComponent as iconAscending } from '@epam/assets/icons/common/table-sort_asc-18.svg';
import { ReactComponent as iconDescending } from '@epam/assets/icons/common/table-sort_desc-18.svg';
import { ReactComponent as iconSort } from '@epam/assets/icons/common/table-swap-18.svg';
import {
  Checkbox,
  FlexCell,
  FlexRow,
  LinkButton,
  SearchInput,
  Text,
  Tooltip,
} from '@epam/promo';
import { createContext, JSX, useMemo, useRef, useState } from 'react';

import { MIN_LENGTH_TO_SEARCH } from 'common/constants';
import { AllStatuses, ItemOneOf, StatusLabels } from 'interfaces';
import { Chips } from 'components/common/Chips';
import { SORT_TYPES, useFilterList, useFilters, useSorting } from 'hooks';
import useResizeObserver from 'use-resize-observer';
import css from './CardFilters.module.scss';
import { Filter } from './Filter';

const SearchContext = createContext<string | null>(null);

const renderDropdownFilterOption = (
  label: string,
  value: boolean,
  update: React.Dispatch<React.SetStateAction<boolean>>,
  total: number,
) => (
  <FlexRow alignItems="center" columnGap="12" padding="12" vPadding="12">
    <FlexCell width="100%">
      <Checkbox
        label={label}
        value={value}
        onValueChange={() => update(!value)}
      />
    </FlexCell>
    {value && (
      <Text color="gray60" cx={css['filter-dropdown__count']} fontSize="14">
        {total}
      </Text>
    )}
  </FlexRow>
);

interface CardFiltersProps<T> {
  children: JSX.Element | [JSX.Element, JSX.Element];
  CardList: ({
    items,
    isSearchActive,
  }: {
    items: T[];
    isSearchActive?: boolean;
  }) => JSX.Element;
  items: T[];
  isFilterVisible?: boolean;
  panelHeight: number;
  favoritesFilter?: {
    isFavorite: (item: T) => boolean;
  };
}

const CardFilters = <T extends ItemOneOf>({
  children,
  CardList,
  items,
  isFilterVisible = true,
  panelHeight,
  favoritesFilter,
}: CardFiltersProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: filtersRef, height: filtersHeight = 0 } =
    useResizeObserver<HTMLDivElement>();
  const [searchValue, setSearchValue] = useState('');
  const { sort, updateSorting } = useSorting();
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoritesDraft, setFavoritesDraft] = useState(false);

  const {
    filterDropdownStates,
    lastActiveFilters,
    isFiltersChanged,
    actions: {
      updateDraftFilter,
      updatePendingApprovalFilter,
      updateApprovalFilter,
      updateLastDraftFilter,
      updateLastPendingApprovalFilter,
      updateLastApprovalFilter,
      clearAllFilters,
      onApplyFilters,
    },
  } = useFilters();
  const { filteredList, searchedList } = useFilterList<T>(
    items,
    searchValue,
    sort,
    lastActiveFilters,
  );

  const isSomeFilterActive =
    lastActiveFilters.filterDraft ||
    lastActiveFilters.filterPendingApproval ||
    lastActiveFilters.filterApproved;

  const favouriteCount = useMemo(() => {
    if (!favoritesFilter) return 0;

    return searchedList.filter((item) => favoritesFilter.isFavorite(item))
      .length;
  }, [favoritesFilter, searchedList]);

  const updateFavoritesDraft = () => setFavoritesDraft((prev) => !prev);

  const isFavoritesSelectionChanged =
    !!favoritesFilter && favoritesDraft !== favoritesOnly;

  const filterApplyEnabled = isFiltersChanged || isFavoritesSelectionChanged;

  const filteredListWithFavorites = useMemo(() => {
    if (!favoritesFilter || !favoritesOnly) return filteredList;

    return filteredList.filter((item) => favoritesFilter.isFavorite(item));
  }, [favoritesFilter, favoritesOnly, filteredList]);

  const isSearchedOrFiltered = useMemo(
    () =>
      searchValue.length >= MIN_LENGTH_TO_SEARCH ||
      sort !== SORT_TYPES.NULL ||
      isSomeFilterActive ||
      favoritesOnly,
    [searchValue, sort, isSomeFilterActive, favoritesOnly],
  );

  const getTotalCountOfTaskByStatus = (status: ItemOneOf['status']) => {
    return searchedList.filter((item) => item.status === status).length;
  };

  return (
    <SearchContext.Provider value={searchValue}>
      <div ref={filtersRef} className={css.filters}>
        <FlexRow ref={containerRef} columnGap="6" cx={css.taskInput__container}>
          <FlexCell cx={css.search__container} grow={1}>
            <SearchInput
              cx={css.search__input}
              debounceDelay={1000}
              placeholder="Search by title"
              value={searchValue}
              onValueChange={(newValue: string) => setSearchValue(newValue)}
            />
          </FlexCell>
          <Tooltip content="Sort by the last update date">
            <LinkButton
              color={sort === SORT_TYPES.NULL ? 'gray60' : 'blue'}
              cx={css['taskInput__filter-icon']}
              icon={
                sort === SORT_TYPES.NULL
                  ? iconSort
                  : sort === SORT_TYPES.DESC
                    ? iconDescending
                    : iconAscending
              }
              onIconClick={updateSorting}
            />
          </Tooltip>
          {isFilterVisible && (
            <Filter
              additionalDraftActive={!!favoritesFilter && favoritesDraft}
              dropdownWidth={containerRef.current?.clientWidth}
              filters={filterDropdownStates}
              isFiltersChanged={filterApplyEnabled}
              onApply={() => {
                onApplyFilters();
                if (favoritesFilter) {
                  setFavoritesOnly(favoritesDraft);
                }
              }}
              onClear={() => {
                clearAllFilters();
                setFavoritesOnly(false);
                setFavoritesDraft(false);
              }}>
              <>
                {renderDropdownFilterOption(
                  StatusLabels.DRAFT,
                  filterDropdownStates.filterDraft,
                  updateDraftFilter,
                  getTotalCountOfTaskByStatus(AllStatuses.DRAFT),
                )}
                {renderDropdownFilterOption(
                  StatusLabels.PENDING_APPROVAL,
                  filterDropdownStates.filterPendingApproval,
                  updatePendingApprovalFilter,
                  getTotalCountOfTaskByStatus(AllStatuses.PENDING_APPROVAL),
                )}
                {renderDropdownFilterOption(
                  StatusLabels.APPROVED,
                  filterDropdownStates.filterApproved,
                  updateApprovalFilter,
                  getTotalCountOfTaskByStatus(AllStatuses.APPROVED),
                )}
                {favoritesFilter &&
                  renderDropdownFilterOption(
                    'Favourite',
                    favoritesDraft,
                    updateFavoritesDraft,
                    favouriteCount,
                  )}
              </>
            </Filter>
          )}
        </FlexRow>
        {(isSomeFilterActive || favoritesOnly) && (
          <div>
            {lastActiveFilters.filterDraft && (
              <Chips
                count={getTotalCountOfTaskByStatus(AllStatuses.DRAFT)}
                label={StatusLabels.DRAFT}
                status={AllStatuses.DRAFT}
                onClose={() => {
                  updateDraftFilter();
                  updateLastDraftFilter();
                }}
              />
            )}
            {lastActiveFilters.filterPendingApproval && (
              <Chips
                count={getTotalCountOfTaskByStatus(
                  AllStatuses.PENDING_APPROVAL,
                )}
                label={StatusLabels.PENDING_APPROVAL}
                status={AllStatuses.PENDING_APPROVAL}
                onClose={() => {
                  updatePendingApprovalFilter();
                  updateLastPendingApprovalFilter();
                }}
              />
            )}
            {lastActiveFilters.filterApproved && (
              <Chips
                count={getTotalCountOfTaskByStatus(AllStatuses.APPROVED)}
                label={StatusLabels.APPROVED}
                status={AllStatuses.APPROVED}
                onClose={() => {
                  updateApprovalFilter();
                  updateLastApprovalFilter();
                }}
              />
            )}
            {favoritesFilter && favoritesOnly && (
              <Chips
                count={favouriteCount}
                label="Favourite"
                leadingSymbol="★"
                onClose={() => {
                  setFavoritesOnly(false);
                  setFavoritesDraft(false);
                }}
              />
            )}
          </div>
        )}
      </div>
      {isSearchedOrFiltered ? (
        filteredListWithFavorites.length ? (
          <div
            style={{
              height: `calc(${panelHeight}px - 60px - ${filtersHeight}px - 24px)`,
            }}>
            <CardList isSearchActive items={filteredListWithFavorites} />
          </div>
        ) : (
          <Text color="gray60" fontSize="14">
            Sorry, no results found, matching your search request
          </Text>
        )
      ) : (
        children
      )}
    </SearchContext.Provider>
  );
};

export { SearchContext };

export default CardFilters;
